import React, { useRef, useEffect } from "react";
import Chart from "chart.js";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";

import { AlertByType } from "../../types";
import { ALERT_TYPE_COLORS } from "../../constants";
import { ChartContainer } from "../../styles";

/** Props for the AlertDistributionChart organism. */
interface AlertDistributionChartProps {
  alertsByType: AlertByType[];
  loading: boolean;
  onBarClick?: (alertType: AlertByType) => void;
}

/**
 * Horizontal bar chart showing the distribution of alerts by type (7 alert types).
 * Uses chart.js v2.x `horizontalBar` type.
 */
const AlertDistributionChart: React.FC<AlertDistributionChartProps> = ({ alertsByType, loading, onBarClick }) => {

  const { t } = useTranslation();
  const chartRef = useRef<Chart | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const onBarClickRef = useRef(onBarClick);

  onBarClickRef.current = onBarClick;

  useEffect(() => {
    // Destroy previous chart instance on data change
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!canvasRef.current || loading || alertsByType.length === 0) return;

    // Sort by count descending
    const sorted = [...alertsByType].sort((a, b) => b.count - a.count);

    const labels = sorted.map((item) =>
      item.description.length > 30 ? `${item.description.substring(0, 30)}…` : item.description
    );

    const data = sorted.map((item) => item.count);
    const colors = sorted.map((_, index) => ALERT_TYPE_COLORS[index % ALERT_TYPE_COLORS.length]);

    chartRef.current = new Chart(canvasRef.current, {
      type: "horizontalBar",
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: colors,
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            ticks: { beginAtZero: true, precision: 0 },
            gridLines: { drawBorder: false }
          }],
          yAxes: [{
            gridLines: { display: false }
          }]
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem) => ` ${tooltipItem.xLabel}`
          }
        },
        onClick: (_event: any, elements: any[]) => {
          if (elements && elements.length > 0) {
            const index = elements[0]._index;

            onBarClickRef.current?.(sorted[index]);
          }
        },
        onHover: (_event: any, elements: any[]) => {
          const canvas = canvasRef.current;

          if (canvas) {
            canvas.style.cursor = elements && elements.length > 0 ? "pointer" : "default";
          }
        }
      }
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [alertsByType, loading]);

  if (!loading && alertsByType.length === 0) {
    return (
      <ChartContainer>
        <div className="chart-title">{t(TravelAnalysisMessages.alertDistributionTitle)}</div>
        <div className="chart-empty">{t(TravelAnalysisMessages.noAlertsInPeriod)}</div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <div className="chart-title">{t(TravelAnalysisMessages.alertDistributionTitle)}</div>
      <div style={{ position: "relative", height: 280 }}>
        <canvas ref={canvasRef} />
      </div>
    </ChartContainer>
  );
};

export default AlertDistributionChart;
